import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { noWhitespaceValidator } from '../../../../shared/utilities/utility-functions';
import { TooltipOptions } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-send-guardians-mass-notification',
  standalone: true,
  imports: [CommonModule, ButtonComponent, ReactiveFormsModule, MultiSelectModule, TooltipModule, DialogModule, InputTextModule, TextareaModule],
  templateUrl: './send-guardians-mass-notification.component.html',
  styleUrl: './send-guardians-mass-notification.component.scss',
})
export class SendGuardiansMassNotificationComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly fb = inject(FormBuilder);

  states = signal<{ name: string; id: number }[]>([]);
  clients = signal<{ name: string; id: number }[]>([]);
  places = signal<{ name: string; id: number }[]>([]);
  loading = signal<boolean>(false);

  // Track form values as signals
  selectedClients = signal<any[]>([]);
  selectedStates = signal<any[]>([]);
  selectedPlaces = signal<any[]>([]);

  // Computed tooltips (only recalculate when selections change)
  clientsTooltip = computed(() => {
    const clients = this.selectedClients()
      .map((client: any) => client.name)
      .join(', ');
    return clients || 'Clients Names';
  });

  statesTooltip = computed(() => {
    const states = this.selectedStates()
      .map((state: any) => state.name)
      .join(', ');
    return states || 'States Names';
  });

  placesTooltip = computed(() => {
    const places = this.selectedPlaces()
      .map((place: any) => place.name)
      .join(', ');
    return places || 'Places Names';
  });

  form!: FormGroup;

  tooltipOptions: TooltipOptions = {
    showDelay: 500,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
  };
  confirmationDialogVisible = false;

  ngOnInit() {
    this.states.set(this.config.data.states?.map((state: string) => ({ name: state, id: state })) ?? []);
    this.clients.set(this.config.data.clients ?? []);
    this.places.set(this.config.data.places ?? []);
    this.initForm();
  }

  openConfirmDialog() {
    this.confirmationDialogVisible = true;
  }

  closeConfirmDialog() {
    this.confirmationDialogVisible = false;
  }

  confirmAction() {
    const body = {
      filters: {
        elasticExpression: {
          filters: [
            {
              field: 'ClientIds',
              operator: 'Equals',
              value: this.selectedClients()
                .map(client => client.id)
                .join(','),
              ignoreCase: '',
            },
            {
              field: 'States',
              operator: 'Equals',
              value: this.selectedStates()
                .map(state => state.id)
                .join(','),
              ignoreCase: '',
            },
            {
              field: 'PlaceIds',
              operator: 'Equals',
              value: this.selectedPlaces()
                .map(place => place.id)
                .join(','),
              ignoreCase: '',
            },
          ],
        },
      },
      title: this.form.get('title')?.value,
      body: this.form.get('body')?.value,
    };

    this.loading.set(true);

    this.requestBuilder.post(`guardians/mass-guardian-notifications`, body).subscribe({
      next: res => {
        this.loading.set(false);
        this.ref.close({ confirmed: true });
      },
      error: err => {
        console.error(err);
        this.loading.set(false);
      },
    });
  }

  clearFormControl(controlName: string) {
    this.form.get(controlName)?.setValue(null);
  }

  initForm() {
    this.form = this.fb.group({
      clients: [null, Validators.required],
      places: [null, Validators.required],
      states: [null, Validators.required],
      title: ['', [Validators.required, noWhitespaceValidator]],
      body: ['', [Validators.required, noWhitespaceValidator]],
    });

    // Track form value changes and update signals
    this.form.get('clients')?.valueChanges.subscribe(value => {
      this.selectedClients.set(value ?? []);
    });

    this.form.get('states')?.valueChanges.subscribe(value => {
      this.selectedStates.set(value ?? []);
    });

    this.form.get('places')?.valueChanges.subscribe(value => {
      this.selectedPlaces.set(value ?? []);
    });
  }
}
